import { Alert, Box, Button, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useNavigate } from "react-router-dom";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CreatorGridService from "./service/CreatorGridService";
import CreatorCrossService from "./service/CreatorCrossService";
import { CONTENT_LANGUAGES, getContentLanguage, setContentLanguage } from "../services/LangPrefs";
import UserService from "../services/UserService";
import Logger from "../services/Logger";
import { trackEvent } from "../services/GoogleAnalytics";
import Greeting from "./Greeting";
import StatsByStateTable from "../admin/pzl_common/stats/StatsByStateTable";

// Aliases for backwards compatibility (component moved to admin/pzl_common/stats/StatsByStateTable)
const CreatorStatsByState = StatsByStateTable;
const CreatorByStateTable = StatsByStateTable;

export default function CreatorMain() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [englishStats, setEnglishStats] = useState(null);
    const [frenchStats, setFrenchStats] = useState(null);
    const [englishStatsLoading, setEnglishStatsLoading] = useState(true);
    const [frenchStatsLoading, setFrenchStatsLoading] = useState(true);

    useEffect(() => {
        trackEvent("entered", { name: "CreatorMain" });
    }, []);

    useEffect(() => {
        const loadUser = async () => {
            try {
                const userData = await UserService.getUserInfo();
                if (userData?.cloudUser) {
                    setUser(userData.cloudUser);
                }
            } catch (error) {
                Logger.error("Error loading user:", error);
            } finally {
                setLoading(false);
            }
        };

        loadUser();
    }, []);

    useEffect(() => {
        const loadAllStats = async () => {
            setEnglishStatsLoading(true);
            setFrenchStatsLoading(true);
            try {
                const [gridStats, crossStats] = await Promise.all([
                    CreatorGridService.getGridStats(),
                    CreatorCrossService.getCrossStats()
                ]);

                const byLang = gridStats?.byLanguage || crossStats?.byLanguage || {};
                const gridByLang = gridStats?.byLanguage || {};
                const crossByLang = crossStats?.byLanguage || {};

                const buildStatsForLang = (lang) => {
                    const g = gridByLang[lang] || {};
                    const c = crossByLang[lang] || {};
                    const gridTotal = g.gridTotal ?? gridStats?.gridPuzzles ?? 0;
                    const crossTotal = c.crossTotal ?? crossStats?.crossPuzzles ?? 0;
                    const gridByType = g.gridByType ?? gridStats?.gridByType ?? {};
                    const crossByType = c.crossByType ?? crossStats?.crossByType ?? {};
                    const gridDisc = gridStats?.gridDiscarded ?? 0;
                    const crossDisc = crossStats?.crossDiscarded ?? 0;
                    return {
                        gridPuzzles: gridTotal,
                        crossPuzzles: crossTotal,
                        totalPuzzles: gridTotal + crossTotal,
                        gridByType,
                        crossByType,
                        gridDiscarded: gridDisc,
                        crossDiscarded: crossDisc,
                        totalDiscarded: gridDisc + crossDisc,
                        gridWordSearchByState: g.gridWordSearchByState,
                        gridHiddenMessageByState: g.gridHiddenMessageByState,
                        crossByState: c.crossByState
                    };
                };

                setEnglishStats(buildStatsForLang('english'));
                setFrenchStats(buildStatsForLang('french'));
            } catch (error) {
                Logger.error("Error loading stats:", error);
                setEnglishStats(null);
                setFrenchStats(null);
            } finally {
                setEnglishStatsLoading(false);
                setFrenchStatsLoading(false);
            }
        };

        loadAllStats();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    const userName = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : user?.name || "Creator";
    
    // Determine locale from user language preference or default to 'en'
    const locale = user?.language === 'french' ? 'fr' : 'en';

    return (
        <Box sx={{ p: 2 }}>
            <Grid container spacing={3} justifyContent="center">
                <Grid size={{ xs: 12 }}>
                    <Box sx={{ 
                        textAlign: "center", 
                        mb: 2, 
                        color: "text.secondary",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "80px",
                        gap: 2
                    }}>
                        <Greeting name={userName} locale={locale} />
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RocketLaunchIcon />}
                            onClick={() => navigate("/creator/guide")}
                            sx={{ whiteSpace: "nowrap" }}
                        >
                            Quick Start
                        </Button>
                    </Box>
                </Grid>

                {/* By State (by language) - CREATOR and MANAGER */}
                {(englishStats || frenchStats) && (
                    <Grid size={{ xs: 12 }}>
                        <StatsByStateTable
                            englishStats={englishStats}
                            frenchStats={frenchStats}
                            hideCrossword={user?.userRole?.toUpperCase() === "CREATOR"}
                        />
                        {user?.userRole?.toUpperCase() === "CREATOR" && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Crossword puzzle is fully managed by system administrator.
                            </Alert>
                        )}
                    </Grid>
                )}

            </Grid>
        </Box>
    );
}

